import { SessionCluster } from '@/types/schema';

import { TreeNode } from '../types';

function sortByNumRunsAndThenName(a: TreeNode, b: TreeNode) {
  const aRuns = Number(a.num_runs) || 0;
  const bRuns = Number(b.num_runs) || 0;
  if (aRuns !== bRuns) {
    return bRuns - aRuns;
  }
  return (a.name || '').localeCompare(b.name || '');
}

// Clusters come with a level property that indicates their depth
// 0 is a leaf (no children)
// 1 is a parent of leaves, 2 is a parent of parents, etc.

// Build tree structure from flat cluster data
export const buildCategoryTree = (clusters: SessionCluster[]): TreeNode[] => {
  if (!clusters?.length) return [];
  const maxLevel = Math.max(...clusters.map((c) => c.level));
  const clusterMap = new Map<string, TreeNode>();

  clusters.forEach((cluster) => {
    const treeNode: TreeNode = {
      ...cluster,
      children: [],
      depth: maxLevel - cluster.level,
      isLeaf: cluster.level === 0, // Level 0 are leaves
    };
    clusterMap.set(cluster.id, treeNode);
  });

  // Set parentClusterNumRuns and add children to parent
  clusters.forEach((cluster) => {
    const node = clusterMap.get(cluster.id);
    if (node && cluster.parent_id) {
      node.parentClusterNumRuns = clusterMap.get(cluster.parent_id)?.num_runs;
      const parent = clusterMap.get(cluster.parent_id);
      if (parent) {
        parent.children?.push(node);
      }
    }
  });

  // Sort children and set indexInParent and numPeers so it knows where it's position is in the parent
  clusterMap.forEach((node) => {
    if (node.children && node.children.length > 0) {
      node.children.sort(sortByNumRunsAndThenName);
      node.children.forEach((child, index) => {
        child.indexInParent = index;
        child.numPeers = node.children!.length; // Number of siblings (including itself)
      });
    }
  });

  // All of this precomputing is for the proportional runs display

  // Calculate totalRunPercentageOfCategory per depth level
  // Group nodes by their depth
  const nodesByDepth = new Map<number, TreeNode[]>();
  clusterMap.forEach((node) => {
    const depth = node.depth;
    if (!nodesByDepth.has(depth)) {
      nodesByDepth.set(depth, []);
    }
    nodesByDepth.get(depth)!.push(node);
  });

  // For each depth level, calculate percentages relative to max run count at that depth
  nodesByDepth.forEach((nodesAtDepth) => {
    const maxRunsAtDepth = Math.max(
      ...nodesAtDepth.map((node) => Number(node.num_runs) || 0)
    );

    // Set totalRunPercentageOfCategory for each node at this depth
    nodesAtDepth.forEach((node) => {
      const runCount = Number(node.num_runs) || 0;
      node.totalRunPercentageOfCategory =
        maxRunsAtDepth > 0 ? (runCount / maxRunsAtDepth) * 100 : 0;
    });
  });

  // Set parentTotalRunPercentageOfCategory for child nodes
  clusterMap.forEach((node) => {
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        child.parentTotalRunPercentageOfCategory =
          node.totalRunPercentageOfCategory;
      });
    }
  });

  const roots = clusters
    .filter((c) => c.level === maxLevel)
    .map((c) => clusterMap.get(c.id)!)
    .filter(Boolean)
    .sort(sortByNumRunsAndThenName);

  const totalRunsInJob = roots.reduce(
    (acc, root) => acc + Number(root.num_runs),
    0
  );
  clusterMap.forEach((node) => {
    node.totalRunsInJob = totalRunsInJob;
  });

  // Set numPeers for root nodes (they are peers with each other)
  roots.forEach((root) => {
    root.numPeers = roots.length;
  });

  return roots;
};
